import type { PblCitizenAuthorizationPayload, PblOcContext } from '@/types/pbl';

export type OcCitizenAuthorizationForm = {
  pbl_oc_office_id: number;
  oc_office_name: string;
  assisting_staff_name: string;
  office_contact: string;
  citizen_surname_en: string;
  citizen_given_name_en: string;
  citizen_surname_zh: string;
  citizen_given_name_zh: string;
  identity_partial: string;
  phone: string;
  email: string;
  email_otp: string;
  email_otp_verified: boolean;
  phone_otp: string;
  phone_otp_verified: boolean;
  residential_address: string;
  pics_agreed: boolean;
  terms_agreed: boolean;
  authorisation_agreed: boolean;
  information_true_declared: boolean;
  enquirer_signature: string;
  enquirer_signature_file_id?: number;
  enquirer_signed_at: string;
  witness_staff_signature: string;
  witness_staff_signature_file_id?: number;
  witness_staff_signed_at: string;
};

export type OcCitizenSignatureRole = 'enquirer' | 'witness';

export const OC_CITIZEN_TEST_STATION_OTP = '777777';

type SignatureUpdate = {
  dataUrl: string;
  fileId: number;
  signedAt: string;
};

export const initialOcCitizenAuthorizationForm: OcCitizenAuthorizationForm = {
  pbl_oc_office_id: 0,
  oc_office_name: '',
  assisting_staff_name: '',
  office_contact: '',
  citizen_surname_en: '',
  citizen_given_name_en: '',
  citizen_surname_zh: '',
  citizen_given_name_zh: '',
  identity_partial: '',
  phone: '',
  email: '',
  email_otp: '',
  email_otp_verified: false,
  phone_otp: '',
  phone_otp_verified: false,
  residential_address: '',
  pics_agreed: false,
  terms_agreed: false,
  authorisation_agreed: false,
  information_true_declared: false,
  enquirer_signature: '',
  enquirer_signed_at: '',
  witness_staff_signature: '',
  witness_staff_signed_at: '',
};

const hasText = (value: string) => value.trim().length > 0;
const hasFileId = (value?: number) => typeof value === 'number' && Number.isFinite(value);
const joinNameParts = (parts: string[], separator: string) => parts.map((part) => part.trim()).filter(Boolean).join(separator);

export const buildOcCitizenFullNameEn = (
  form: Pick<OcCitizenAuthorizationForm, 'citizen_surname_en' | 'citizen_given_name_en'>
) => joinNameParts([form.citizen_surname_en, form.citizen_given_name_en], ' ');

export const buildOcCitizenFullNameZh = (
  form: Pick<OcCitizenAuthorizationForm, 'citizen_surname_zh' | 'citizen_given_name_zh'>
) => joinNameParts([form.citizen_surname_zh, form.citizen_given_name_zh], '');

export const applyPblOcContextToAuthorizationForm = (
  form: OcCitizenAuthorizationForm,
  context?: PblOcContext | null
): OcCitizenAuthorizationForm => {
  const office = context?.default_office || context?.offices?.[0];

  if (!office) return form;

  return {
    ...form,
    pbl_oc_office_id: office.pbl_oc_office_id,
    oc_office_name: office.oc_office_name,
    assisting_staff_name: context?.staff.name || office.assisting_staff_name || '',
    office_contact: office.office_contact || '',
  };
};

export const isOcCitizenAuthorizationComplete = (form: OcCitizenAuthorizationForm) =>
  form.pbl_oc_office_id > 0 &&
  hasText(form.oc_office_name) &&
  hasText(form.assisting_staff_name) &&
  hasText(form.citizen_surname_en) &&
  hasText(form.citizen_given_name_en) &&
  hasText(form.citizen_surname_zh) === hasText(form.citizen_given_name_zh) &&
  hasText(form.identity_partial) &&
  (hasText(form.phone) || hasText(form.email)) &&
  (!hasText(form.email) || (hasText(form.email_otp) && form.email_otp_verified)) &&
  (!hasText(form.phone) || (hasText(form.phone_otp) && form.phone_otp_verified)) &&
  hasText(form.residential_address) &&
  form.pics_agreed &&
  form.terms_agreed &&
  form.authorisation_agreed &&
  form.information_true_declared &&
  hasFileId(form.enquirer_signature_file_id) &&
  hasText(form.enquirer_signed_at) &&
  hasFileId(form.witness_staff_signature_file_id) &&
  hasText(form.witness_staff_signed_at);

export const signOcCitizenAuthorization = (
  form: OcCitizenAuthorizationForm,
  role: OcCitizenSignatureRole,
  signature: SignatureUpdate
): OcCitizenAuthorizationForm => {
  if (role === 'enquirer') {
    return {
      ...form,
      enquirer_signature: signature.dataUrl,
      enquirer_signature_file_id: signature.fileId,
      enquirer_signed_at: signature.signedAt,
    };
  }

  return {
    ...form,
    witness_staff_signature: signature.dataUrl,
    witness_staff_signature_file_id: signature.fileId,
    witness_staff_signed_at: signature.signedAt,
  };
};

export const buildOcCitizenAuthorizationPayload = (form: OcCitizenAuthorizationForm): PblCitizenAuthorizationPayload => {
  const email = form.email.trim();
  const payload: PblCitizenAuthorizationPayload = {
    pbl_oc_office_id: form.pbl_oc_office_id,
    oc_office_name: form.oc_office_name,
    assisting_staff_name: form.assisting_staff_name,
    office_contact: form.office_contact,
    citizen_surname_en: form.citizen_surname_en,
    citizen_given_name_en: form.citizen_given_name_en,
    citizen_surname_zh: form.citizen_surname_zh,
    citizen_given_name_zh: form.citizen_given_name_zh,
    identity_partial: form.identity_partial,
    residential_address: form.residential_address,
    pics_agreed: form.pics_agreed,
    terms_agreed: form.terms_agreed,
    authorisation_agreed: form.authorisation_agreed,
    information_true_declared: form.information_true_declared,
    enquirer_signature_file_id: form.enquirer_signature_file_id,
    enquirer_signed_at: form.enquirer_signed_at,
    witness_staff_signature_file_id: form.witness_staff_signature_file_id,
    witness_staff_signed_at: form.witness_staff_signed_at,
  };

  if (form.phone.trim()) {
    payload.phone = form.phone.trim();
    payload.phone_otp = form.phone_otp;
  }

  if (email) {
    payload.email = email;
    payload.email_otp = form.email_otp;
  }

  return payload;
};
